import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Folder, FolderPlus, Upload, FileText, Image as ImageIcon, File as FileIcon,
  Download, Eye, Trash2, ChevronRight, Home, Loader2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";

const SECTIONS = [
  { id: "documente", label: "Documente interne" },
  { id: "proceduri", label: "Proceduri interne" },
  { id: "regulament", label: "Regulamentul intern" },
];

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct === "application/pdf") return FileText;
  return FileIcon;
}

function humanSize(n = 0) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ReglementariTab() {
  const { hasPerm } = useErpAuth();
  const canAdmin = hasPerm("reglementari.administrare");
  const [section, setSection] = useState("documente");
  const [folderId, setFolderId] = useState(null);
  const [data, setData] = useState({ folders: [], docs: [], breadcrumb: [] });
  const [loading, setLoading] = useState(true);

  const [folderDialog, setFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [savingFolder, setSavingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFolder, setUploadingFolder] = useState(false);

  const [preview, setPreview] = useState(null); // {url|html, kind, title}
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = folderId ? { folder_id: folderId } : {};
      const { data } = await erpApi.get(`/reglementari/${section}`, { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [section, folderId]);

  useEffect(() => { load(); }, [load]);

  const switchSection = (id) => { setSection(id); setFolderId(null); };

  const openFolder = (id) => setFolderId(id);
  const goCrumb = (id) => setFolderId(id);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setSavingFolder(true);
    try {
      await erpApi.post("/reglementari/folders", { section, name, parent_id: folderId });
      toast.success("Folder creat");
      setNewFolderName("");
      setFolderDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingFolder(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const params = {};
      if (folderId) params.folder_id = folderId;
      await erpApi.post(`/reglementari/${section}/upload`, form, { params });
      toast.success("Document încărcat");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onUploadFolder = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingFolder(true);
    const form = new FormData();
    files.forEach((file) => {
      form.append("files", file);
      form.append("paths", file.webkitRelativePath || file.name);
    });
    try {
      const params = {};
      if (folderId) params.folder_id = folderId;
      const { data } = await erpApi.post(`/reglementari/${section}/upload-folder`, form, { params });
      toast.success(`Folder încărcat: ${data.uploaded} fișiere, ${data.folders} foldere` +
        (data.skipped ? ` (${data.skipped} ignorate)` : ""));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setUploadingFolder(false);
    }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm("Ștergi folderul și tot conținutul lui?")) return;
    try {
      await erpApi.delete(`/reglementari/folders/${id}`);
      toast.success("Folder șters");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Ștergi documentul?")) return;
    try {
      await erpApi.delete(`/reglementari/doc/${id}`);
      toast.success("Document șters");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const openPreview = async (doc) => {
    setPreviewLoading(true);
    const ext = (doc.filename || "").toLowerCase().split(".").pop();
    try {
      if (ext === "docx" || ext === "xlsx") {
        const res = await erpApi.get(`/reglementari/doc/${doc.id}/preview`, { responseType: "text" });
        setPreview({ kind: "html", html: res.data, title: doc.title });
      } else if ((doc.content_type || "").startsWith("image/")) {
        const res = await erpApi.get(`/reglementari/doc/${doc.id}/file`, { responseType: "blob" });
        setPreview({ kind: "image", url: URL.createObjectURL(res.data), title: doc.title });
      } else if (doc.content_type === "application/pdf" || ext === "pdf") {
        const res = await erpApi.get(`/reglementari/doc/${doc.id}/file`, { responseType: "blob" });
        setPreview({ kind: "pdf", url: URL.createObjectURL(res.data), title: doc.title });
      } else {
        setPreview({ kind: "unsupported", title: doc.title, doc });
      }
    } catch (e) {
      toast.error("Nu s-a putut deschide documentul");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await erpApi.get(`/reglementari/doc/${doc.id}/file`, {
        responseType: "blob", params: { download: 1 },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.title || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Descărcare eșuată");
    }
  };

  return (
    <div className="space-y-5" data-testid="reglementari-tab">
      {/* Sub-taburi */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => switchSection(s.id)}
            data-testid={`reglementari-section-${s.id}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${
              section === s.id ? "bg-[#2563EB] text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Toolbar: breadcrumb + acțiuni admin */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-slate-600 min-w-0 flex-wrap" data-testid="reglementari-breadcrumb">
          <button onClick={() => goCrumb(null)} className="flex items-center gap-1 hover:text-[#2563EB] font-medium">
            <Home className="w-4 h-4" /> {SECTIONS.find((s) => s.id === section)?.label}
          </button>
          {data.breadcrumb.map((c) => (
            <span key={c.id} className="flex items-center gap-1 min-w-0">
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              <button onClick={() => goCrumb(c.id)} className="hover:text-[#2563EB] truncate max-w-[160px]">{c.name}</button>
            </span>
          ))}
        </div>

        {canAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setFolderDialog(true)} data-testid="reglementari-new-folder-btn">
              <FolderPlus className="w-4 h-4 mr-1.5" /> Folder nou
            </Button>
            <label>
              <input type="file" className="hidden" onChange={onUpload}
                     accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx"
                     data-testid="reglementari-upload-input" />
              <Button asChild size="sm" className="bg-[#2563EB] hover:bg-[#1d4ed8]" disabled={uploading}>
                <span data-testid="reglementari-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  Încarcă fișier
                </span>
              </Button>
            </label>
            <label>
              <input type="file" className="hidden" onChange={onUploadFolder}
                     webkitdirectory="" directory="" mozdirectory="" multiple
                     data-testid="reglementari-upload-folder-input" />
              <Button asChild size="sm" variant="outline" disabled={uploadingFolder}>
                <span data-testid="reglementari-upload-folder-btn">
                  {uploadingFolder ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-1.5" />}
                  Încarcă folder
                </span>
              </Button>
            </label>
          </div>
        )}
      </div>

      {/* Conținut */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
      ) : data.folders.length === 0 && data.docs.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="reglementari-empty">
          <Folder className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Niciun folder sau document aici.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden" data-testid="reglementari-list">
          {data.folders.map((f) => (
            <div key={f.id} data-testid={`reglementari-folder-${f.id}`}
                 className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                 onClick={() => openFolder(f.id)}>
              <Folder className="w-5 h-5 text-amber-500 shrink-0" />
              <span className="flex-1 text-sm font-semibold text-slate-800 truncate">{f.name}</span>
              <span className="text-xs text-slate-400 shrink-0">Folder</span>
              {canAdmin && (
                <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }}
                        data-testid={`reglementari-folder-delete-${f.id}`}
                        className="text-slate-300 hover:text-[#DC2626] transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
          ))}

          {data.docs.map((d) => {
            const Icon = fileIcon(d.content_type);
            return (
              <div key={d.id} data-testid={`reglementari-doc-${d.id}`}
                   className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                <Icon className="w-5 h-5 text-[#2563EB] shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-800 truncate" title={d.title}>{d.title}</span>
                </span>
                <span className="text-xs text-slate-400 shrink-0 w-16 text-right">{humanSize(d.size)}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => openPreview(d)}
                          data-testid={`reglementari-preview-${d.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Vezi
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => downloadDoc(d)}
                          data-testid={`reglementari-download-${d.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  {canAdmin && (
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-300 hover:text-[#DC2626]"
                            onClick={() => deleteDoc(d.id)} data-testid={`reglementari-doc-delete-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog folder nou */}
      <Dialog open={folderDialog} onOpenChange={setFolderDialog}>
        <DialogContent data-testid="reglementari-folder-dialog">
          <DialogHeader>
            <DialogTitle>Folder nou</DialogTitle>
            <DialogDescription>Creează un folder în „{SECTIONS.find((s) => s.id === section)?.label}".</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="folder-name">Nume folder</Label>
            <Input id="folder-name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                   placeholder="ex. Procedură ITP" className="mt-1.5" data-testid="reglementari-folder-name-input"
                   onKeyDown={(e) => e.key === "Enter" && createFolder()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog(false)}>Anulează</Button>
            <Button onClick={createFolder} disabled={savingFolder || !newFolderName.trim()}
                    className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="reglementari-folder-save-btn">
              {savingFolder && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Creează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      {previewLoading && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" data-testid="reglementari-preview-modal">
          <div className="flex items-center justify-between px-4 py-3 bg-white">
            <p className="font-semibold text-slate-800 truncate">{preview.title}</p>
            <button onClick={closePreview} data-testid="reglementari-preview-close"
                    className="text-slate-500 hover:text-slate-800"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 overflow-auto bg-slate-800 flex items-center justify-center p-2">
            {preview.kind === "image" ? (
              <img src={preview.url} alt={preview.title} className="max-w-full max-h-full object-contain" />
            ) : preview.kind === "pdf" ? (
              <iframe src={preview.url} title={preview.title} className="w-full h-full bg-white" />
            ) : preview.kind === "html" ? (
              <iframe srcDoc={preview.html} title={preview.title} className="w-full h-full bg-white" />
            ) : (
              <div className="text-center text-white">
                <FileIcon className="w-12 h-12 mx-auto mb-3 opacity-70" />
                <p className="text-sm mb-4">Previzualizarea nu este disponibilă pentru acest tip de fișier.<br/>Descarcă fișierul pentru a-l deschide.</p>
                {preview.doc && (
                  <Button size="sm" className="bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={() => downloadDoc(preview.doc)}>
                    <Download className="w-4 h-4 mr-1.5" /> Descarcă
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
